/**
 * Page Context Detector Service
 *
 * Detects the current page context and extracts relevant data for PDF export.
 * Analyzes the current route, application state, and available data to determine
 * what type of PDF should be generated and what data to include.
 */

// Import domain services to get actual data with graceful fallback
let daysService, placesService, accommodationService, travelService, documentService;

// Import Firebase service for AI resources
let getAIResources;

// Import resource deletion service for deleted AI resources
let loadDeletedResourcesFromFirebase;

// Async service loading function for ES6 modules
async function loadServices() {
  try {
    const daysModule = await import('@trip-planner-monorepo/days');
    daysService = daysModule.daysService;
  } catch (error) {
    console.warn('Days service not available for PDF export:', error.message);
  }

  try {
    const placesModule = await import('@trip-planner-monorepo/places');
    placesService = placesModule.placesService;
    loadDeletedResourcesFromFirebase = placesModule.loadDeletedResourcesFromFirebase;
  } catch (error) {
    console.warn('Places service not available for PDF export:', error.message);
  }

  try {
    const accommodationModule = await import('@trip-planner-monorepo/accommodation');
    accommodationService = accommodationModule.accommodationService;
  } catch (error) {
    console.warn('Accommodation service not available for PDF export:', error.message);
  }

  try {
    const travelModule = await import('@trip-planner-monorepo/travel');
    travelService = travelModule.travelService;
  } catch (error) {
    console.warn('Travel service not available for PDF export:', error.message);
  }

  try {
    const documentsModule = await import('@trip-planner-monorepo/documents');
    documentService = documentsModule.documentService;
  } catch (error) {
    console.warn('Document service not available for PDF export:', error.message);
  }

  try {
    const firestoreModule = await import('@trip-planner-monorepo/shared/firebase/firestoreService');
    getAIResources = firestoreModule.getAIResources;
  } catch (error) {
    console.warn('AI Resources service not available for PDF export:', error.message);
  }
}

// Flag to track if services are loaded
let servicesLoaded = false;

class PageContextDetector {
  constructor() {
    this.supportedPages = [
      'Days List',
      'Day Detail',
      'Places List',
      'Place Detail',
      'Travel List',
      'Travel Detail',
      'Accommodation List',
      'Accommodation Detail',
      'Documents List',
      'Document Detail',
      'Extras List'
    ];
  }

  /**
   * Detect current page context and extract relevant data
   * @param {Object} context - Application context
   * @param {string} context.pathname - Current route pathname
   * @param {Object} context.trip - Current trip data
   * @param {Object} context.user - Current user data
   * @param {Object} context.data - Page-specific data (optional)
   * @returns {Promise<Object>} Page context information
   */
  async detectContext(context) {
    try {
      // Ensure services are loaded before attempting to use them
      if (!servicesLoaded) {
        await loadServices();
        servicesLoaded = true;
      }

      const pageType = this.detectPageType(context.pathname);
      const routeParams = this.extractRouteParameters(context.pathname);

      // Get data based on page type
      const data = await this.extractPageData(pageType, routeParams, context);

      // Generate page title with extracted data - FIX FOR TITLE GENERATION
      const contextWithData = { ...context, data: data };
      const title = this.generatePageTitle(pageType, routeParams, contextWithData);

      return {
        type: pageType,
        title: title,
        data: data,
        tripName: context.trip?.name || 'Trip',
        tripId: context.trip?.id,
        userId: context.user?.uid,
        pathname: context.pathname,
        routeParams: routeParams,
        detectedAt: new Date().toISOString()
      };

    } catch (error) {
      console.error('Page context detection failed:', error);
      throw new Error(`Failed to detect page context: ${error.message}`);
    }
  }

  /**
   * Detect page type from pathname
   * @param {string} pathname - Current route pathname
   * @returns {string} Page type identifier
   */
  detectPageType(pathname) {
    // Individual detail pages (with IDs) - Support both nested (/trips/{tripId}/entity/{entityId}) and legacy (/entity/{id}) routes
    if (pathname.match(/\/trips\/[^\/]+\/days\/[^\/]+$/) || pathname.match(/\/day\/[^\/]+$/)) return 'Day Detail';
    if (pathname.match(/\/trips\/[^\/]+\/places\/[^\/]+$/) || pathname.match(/\/place\/[^\/]+$/)) return 'Place Detail';
    if (pathname.match(/\/trips\/[^\/]+\/travel\/[^\/]+$/) || pathname.match(/\/travel\/[^\/]+$/)) return 'Travel Detail';
    if (pathname.match(/\/trips\/[^\/]+\/accommodation\/[^\/]+$/) || pathname.match(/\/accommodation\/[^\/]+$/)) return 'Accommodation Detail';
    if (pathname.match(/\/trips\/[^\/]+\/documents\/[^\/]+$/) || pathname.match(/\/document\/[^\/]+$/)) return 'Document Detail';

    // List pages
    if (pathname.includes('/days')) return 'Days List';
    if (pathname.includes('/places')) return 'Places List';
    if (pathname.includes('/travel')) return 'Travel List';
    if (pathname.includes('/accommodation')) return 'Accommodation List';
    if (pathname.includes('/documents')) return 'Documents List';
    if (pathname.includes('/extras')) return 'Extras List';

    // Default trip overview for main trip pages
    if (pathname.includes('/trips/')) return 'Days List';

    throw new Error(`Unsupported page type for pathname: ${pathname}`);
  }

  /**
   * Extract route parameters from pathname
   * @param {string} pathname - Current route pathname
   * @returns {Object} Route parameters
   */
  extractRouteParameters(pathname) {
    const params = {};

    // Extract trip ID
    const tripMatch = pathname.match(/\/trips\/([^\/]+)/);
    if (tripMatch) {
      params.tripId = tripMatch[1];
    }

    // Extract entity ID for detail pages - Support both nested and legacy route patterns
    const dayMatch = pathname.match(/\/trips\/[^\/]+\/days\/([^\/]+)/) || pathname.match(/\/day\/([^\/]+)/);
    if (dayMatch) {
      params.dayId = dayMatch[1];
    }

    const placeMatch = pathname.match(/\/trips\/[^\/]+\/places\/([^\/]+)/) || pathname.match(/\/place\/([^\/]+)/);
    if (placeMatch) {
      params.placeId = placeMatch[1];
    }

    const travelMatch = pathname.match(/\/trips\/[^\/]+\/travel\/([^\/]+)/) || pathname.match(/\/travel\/([^\/]+)/);
    if (travelMatch) {
      params.travelId = travelMatch[1];
    }

    const accommodationMatch = pathname.match(/\/trips\/[^\/]+\/accommodation\/([^\/]+)/) || pathname.match(/\/accommodation\/([^\/]+)/);
    if (accommodationMatch) {
      params.accommodationId = accommodationMatch[1];
    }

    const documentMatch = pathname.match(/\/trips\/[^\/]+\/documents\/([^\/]+)/) || pathname.match(/\/document\/([^\/]+)/);
    if (documentMatch) {
      params.documentId = documentMatch[1];
    }

    return params;
  }

  /**
   * Extract page-specific data based on page type and context
   * @param {string} pageType - Detected page type
   * @param {Object} routeParams - Route parameters
   * @param {Object} context - Application context
   * @returns {Promise<*>} Extracted data
   */
  async extractPageData(pageType, routeParams, context) {
    switch (pageType) {
      case 'Days List':
        return await this.extractDaysListData(context);

      case 'Day Detail':
        return await this.extractDayDetailData(routeParams.dayId, context);

      case 'Places List':
        return await this.extractPlacesListData(context);

      case 'Place Detail':
        return await this.extractPlaceDetailData(routeParams.placeId, context);

      case 'Travel List':
        return await this.extractTravelListData(context);

      case 'Travel Detail':
        return await this.extractTravelDetailData(routeParams.travelId, context);

      case 'Accommodation List':
        return await this.extractAccommodationListData(context);

      case 'Accommodation Detail':
        return await this.extractAccommodationDetailData(routeParams.accommodationId, context);

      case 'Documents List':
        return await this.extractDocumentsListData(context);

      case 'Document Detail':
        return await this.extractDocumentDetailData(routeParams.documentId, context);

      case 'Extras List':
        return await this.extractExtrasListData(context);

      default:
        throw new Error(`Data extraction not implemented for page type: ${pageType}`);
    }
  }

  /**
   * Generate appropriate page title - FIXED CONTEXT DETECTION
   * @param {string} pageType - Page type
   * @param {Object} routeParams - Route parameters
   * @param {Object} context - Application context
   * @returns {string} Generated title
   */
  generatePageTitle(pageType, routeParams, context) {
    const tripName = context.trip?.name || 'Trip';

    switch (pageType) {
      case 'Days List':
        return `${tripName} - Daily Itinerary`;
      case 'Day Detail':
        // Generate day title in format: "Day 3: Wed, 03 Sept 2025" (same as Day Detail page)
        console.log('PDF Title Generation Debug - Day Detail:', {
          dayId: routeParams.dayId,
          hasContextData: !!context.data,
          contextData: context.data
        });
        if (routeParams.dayId && context.data) {
          const dayData = context.data;

          // Get day number (from dayNumber field or calculate from position)
          let dayNumber = dayData.dayNumber;
          if (!dayNumber && dayData.order !== undefined) {
            dayNumber = dayData.order + 1;
          }
          if (!dayNumber) {
            dayNumber = '?';
          }

          // Format date (same as Day Detail page uses)
          let formattedDate = 'Date not set';
          if (dayData.date) {
            try {
              const date = new Date(dayData.date);
              formattedDate = date.toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: '2-digit'
              });
            } catch (error) {
              formattedDate = 'Date not set';
            }
          }

          // Add location if available
          let locationPart = '';
          if (dayData.location) {
            locationPart = ` - ${this.sanitizeTextForPDF(dayData.location)}`;
          }

          // Sanitize title text for PDF compatibility
          const originalTitle = `Day ${dayNumber}: ${formattedDate}${locationPart}`;
          const cleanTitle = this.sanitizeTextForPDF(originalTitle);
          console.log('PDF Title Sanitization Debug:', {
            original: originalTitle,
            sanitized: cleanTitle,
            changed: originalTitle !== cleanTitle
          });
          return cleanTitle;
        }
        return `${tripName} - Day Details`;
      case 'Places List':
        return `${tripName} - Places & Locations`;
      case 'Place Detail':
        // Extract specific place info with geographic context on second line
        console.log('PDF Title Generation Debug - Place Detail:', {
          placeId: routeParams.placeId,
          hasContextData: !!context.data,
          contextDataKeys: context.data ? Object.keys(context.data) : null,
          placeName: context.data?.name,
          placeTitle: context.data?.title,
          placeContext: context.data?.context
        });

        if (routeParams.placeId && context.data) {
          let placeName = context.data.name || context.data.title;
          let geographicContext = context.data.context;

          console.log('PDF Title Generation Debug - Initial Values:', {
            placeName,
            geographicContext
          });

          if (placeName) {
            // Handle case where place name contains parentheses with geographic info
            // Format: "Liverpool (Liverpool City Region, England, United Kingdom)"
            // Should become: "Liverpool\nLiverpool City Region, England, United Kingdom"
            const parenthesesMatch = placeName.match(/^(.+?)\s*\((.+)\)$/);
            console.log('PDF Title Generation Debug - Parentheses Match:', {
              placeName,
              parenthesesMatch: !!parenthesesMatch,
              match1: parenthesesMatch?.[1],
              match2: parenthesesMatch?.[2]
            });

            if (parenthesesMatch) {
              // Extract the core place name and the parentheses content
              const coreName = parenthesesMatch[1].trim();
              const parenthesesContent = parenthesesMatch[2].trim();

              // Use core name for first line, parentheses content for second line
              placeName = coreName;
              geographicContext = parenthesesContent;

              console.log('PDF Title Generation Debug - After Parentheses Processing:', {
                coreName,
                parenthesesContent,
                placeName,
                geographicContext
              });
            }

            // Build title with place name and geographic context
            let title = `${tripName} - ${this.sanitizeTextForPDF(placeName)}`;

            // Add geographic context if available
            if (geographicContext) {
              title += `\n${this.sanitizeTextForPDF(geographicContext)}`;
            }

            console.log('PDF Title Generation Debug - Final Title:', {
              title,
              containsNewline: title.includes('\n'),
              titleLength: title.length,
              splitLines: title.split('\n')
            });

            return title;
          }
        }
        console.log('PDF Title Generation Debug - Fallback to generic title');
        return `${tripName} - Place Details`;
      case 'Travel List':
        return `${tripName} - Travel Arrangements`;
      case 'Travel Detail':
        // Generate specific travel title: "tp3 Trip 1 - Air Canada AC321, Los Angeles to Tokyo (11 Aug 2025)"
        console.log('PDF Title Generation Debug - Travel Detail:', {
          travelId: routeParams.travelId,
          hasContextData: !!context.data,
          contextData: context.data
        });

        if (routeParams.travelId && context.data) {
          const travelData = context.data;
          const details = travelData.details || {};

          // Build title components based on travel type
          let travelTitle = '';

          if (travelData.type === 'flight') {
            // Flight format: "Airline FlightNumber, Origin to Destination (Date)"
            const airline = details.airline || 'Unknown Airline';
            const flightNumber = details.flightNumber || '';
            const departureCity = details.departureCity || details.departureAirport || 'Unknown';
            const arrivalCity = details.arrivalCity || details.arrivalAirport || 'Unknown';

            // Format date from departureTime
            let dateStr = '';
            if (details.departureTime) {
              try {
                const date = new Date(details.departureTime);
                const day = date.getDate();
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                const year = date.getFullYear();
                dateStr = `${day} ${month} ${year}`;
              } catch (e) {
                console.warn('Error formatting flight date:', e);
              }
            }

            // Construct title: "Air Canada AC321, Los Angeles to Tokyo (11 Aug 2025)"
            travelTitle = `${airline}${flightNumber ? ' ' + flightNumber : ''}, ${departureCity} to ${arrivalCity}`;
            if (dateStr) {
              travelTitle += ` (${dateStr})`;
            }
          } else if (travelData.type === 'train') {
            // Train format: "Operator TrainNumber, Origin to Destination (Date)"
            const operator = details.operator || 'Unknown Operator';
            const trainNumber = details.trainNumber || '';
            const departureStation = details.departureStation || 'Unknown';
            const arrivalStation = details.arrivalStation || 'Unknown';

            let dateStr = '';
            if (details.departureTime) {
              try {
                const date = new Date(details.departureTime);
                const day = date.getDate();
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                const year = date.getFullYear();
                dateStr = `${day} ${month} ${year}`;
              } catch (e) {
                console.warn('Error formatting train date:', e);
              }
            }

            travelTitle = `${operator}${trainNumber ? ' ' + trainNumber : ''}, ${departureStation} to ${arrivalStation}`;
            if (dateStr) {
              travelTitle += ` (${dateStr})`;
            }
          } else if (travelData.type === 'bus') {
            // Bus format: "Operator ServiceNumber, Origin to Destination (Date)"
            const operator = details.operator || 'Unknown Operator';
            const serviceNumber = details.serviceNumber || '';
            const departureStop = details.departureStop || 'Unknown';
            const arrivalStop = details.arrivalStop || 'Unknown';

            let dateStr = '';
            if (details.departureTime) {
              try {
                const date = new Date(details.departureTime);
                const day = date.getDate();
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                const year = date.getFullYear();
                dateStr = `${day} ${month} ${year}`;
              } catch (e) {
                console.warn('Error formatting bus date:', e);
              }
            }

            travelTitle = `${operator}${serviceNumber ? ' ' + serviceNumber : ''}, ${departureStop} to ${arrivalStop}`;
            if (dateStr) {
              travelTitle += ` (${dateStr})`;
            }
          } else {
            // Generic travel format - use summary or fallback
            travelTitle = travelData.summary || this.formatTravelRoute(travelData) || 'Travel';
          }

          console.log('PDF Title Generation Debug - Travel title:', {
            travelTitle,
            type: travelData.type
          });

          if (travelTitle) {
            return `${tripName} - ${this.sanitizeTextForPDF(travelTitle)}`;
          }
        }

        console.log('PDF Title Generation Debug - Fallback to generic travel title');
        return `${tripName} - Travel Details`;
      case 'Accommodation List':
        return `${tripName} - Accommodation`;
      case 'Accommodation Detail':
        // Generate accommodation title in format: "tp3 Trip 1 - Hotel Name - Place Name" (hotel name + place name)
        console.log('PDF Title Generation Debug - Accommodation Detail:', {
          accommodationId: routeParams.accommodationId,
          hasContextData: !!context.data,
          contextData: context.data
        });
        if (routeParams.accommodationId && context.data) {
          const accommodationData = context.data;

          // Get accommodation name from details.name (primary) or summary fallback
          let accommodationName = '';
          if (accommodationData.details && accommodationData.details.name) {
            accommodationName = accommodationData.details.name;
          } else if (accommodationData.summary) {
            // Summary often contains "Hotel Name: City, Country" format, extract just the hotel name
            const summaryParts = accommodationData.summary.split(':');
            accommodationName = summaryParts[0].trim();
          } else if (accommodationData.name) {
            accommodationName = accommodationData.name;
          }

          // Get place name from various possible locations
          let placeName = '';
          if (accommodationData.details && accommodationData.details.city) {
            // Try to get full place context like "Seville, Spain"
            const cityName = accommodationData.details.city;
            const countryName = accommodationData.details.country;
            placeName = countryName ? `${cityName}, ${countryName}` : cityName;
          } else if (accommodationData.summary) {
            // Extract location from summary format "Hotel Name: City, Country"
            const summaryParts = accommodationData.summary.split(':');
            if (summaryParts.length > 1) {
              placeName = summaryParts[1].trim();
            }
          }

          console.log('PDF Title Generation Debug - Extracted names:', {
            accommodationName,
            placeName,
            detailsCity: accommodationData.details?.city,
            detailsCountry: accommodationData.details?.country,
            summary: accommodationData.summary
          });

          if (accommodationName) {
            // Include place name if available: "tp3 Trip 1 - Another hotel - Seville, Spain"
            const titleWithPlace = placeName ?
              `${tripName} - ${this.sanitizeTextForPDF(accommodationName)} - ${this.sanitizeTextForPDF(placeName)}` :
              `${tripName} - ${this.sanitizeTextForPDF(accommodationName)}`;
            return titleWithPlace;
          }
        }
        console.log('PDF Title Generation Debug - Fallback to generic accommodation title');
        return `${tripName} - Accommodation Details`;
      case 'Documents List':
        return `${tripName} - Documents`;
      case 'Document Detail':
        // Extract specific document info if available
        if (routeParams.documentId && context.data) {
          const documentName = context.data.name || context.data.title || context.data.filename;
          if (documentName) {
            return `${tripName} - ${this.sanitizeTextForPDF(documentName)}`;
          }
        }
        return `${tripName} - Document Details`;
      case 'Extras List':
        return `${tripName} - Additional Information`;
      default:
        return `${tripName} - Export`;
    }
  }

  /**
   * Format travel route for title display
   * @param {Object} travelData - Travel data
   * @returns {string} Formatted route
   */
  formatTravelRoute(travelData) {
    const origin = travelData.origin || travelData.from;
    const destination = travelData.destination || travelData.to;
    if (origin && destination) {
      return `${origin} to ${destination}`;
    }
    return null;
  }

  // Data extraction methods for different page types

  async extractDaysListData(context) {
    try {
      // Get days data from the actual daysService if available
      if (daysService && context.tripId && context.user?.uid) {
        const daysData = await daysService.fetchDays(context.user.uid, context.tripId);
        return Array.isArray(daysData) ? daysData : [];
      }

      // Fallback to context data or local storage
      let data = context.data?.days || context.trip?.days || [];

      if (!data || data.length === 0) {
        try {
          const storedData = this.getFromLocalStorage('trip-days');
          if (storedData) {
            data = JSON.parse(storedData);
          }
        } catch (error) {
          console.warn('Could not retrieve days data from local storage:', error);
        }
      }

      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('Could not retrieve days data from service:', error);
      return [];
    }
  }

  async extractDayDetailData(dayId, context) {
    try {
      // First try to get specific day data from service
      if (daysService && context.tripId && context.user?.uid) {
        // Use fetchDays and find the specific day (fetchDay method doesn't exist)
        const allDays = await daysService.fetchDays(context.user.uid, context.tripId);
        const dayData = allDays?.find(day => day.id === dayId);
        if (dayData) {
          return dayData;
        }
      }

      // Fallback to searching in days list
      const daysData = await this.extractDaysListData(context);
      const dayData = daysData.find(day => day.id === dayId);

      // If still no data, try context.data as last resort
      if (!dayData && context.data) {
        return context.data;
      }

      return dayData || null;
    } catch (error) {
      console.warn('Could not retrieve day detail data:', error);
      // Last resort: return context.data if available
      return context.data || null;
    }
  }

  async extractPlacesListData(context) {
    try {
      // Get places data from the actual placesService if available
      if (placesService && context.tripId && context.user?.uid) {
        const placesData = await placesService.fetchPlaces(context.user.uid, context.tripId);
        return Array.isArray(placesData) ? placesData : [];
      }

      // Fallback to context data or local storage
      let data = context.data?.places || context.trip?.places || [];

      if (!data || data.length === 0) {
        try {
          const storedData = this.getFromLocalStorage('trip-places');
          if (storedData) {
            data = JSON.parse(storedData);
          }
        } catch (error) {
          console.warn('Could not retrieve places data from local storage:', error);
        }
      }

      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('Could not retrieve places data from service:', error);
      return [];
    }
  }

  async extractPlaceDetailData(placeId, context) {
    const placesData = await this.extractPlacesListData(context);
    const placeData = placesData.find(place => place.id === placeId) || null;

    if (placeData) {
      // Also get days data for Related Days section (same as PlaceDetails.jsx useDays hook)
      try {
        const daysData = await this.extractDaysListData(context);
        placeData._daysData = daysData; // Add days data to place data for PDF generation
      } catch (error) {
        console.warn('Could not retrieve days data for place details:', error);
        placeData._daysData = [];
      }

      // Fetch AI-generated resources from Firebase
      try {
        if (getAIResources && context.user?.uid && context.tripId) {
          // Fetch active AI resources
          const aiResourcesData = await getAIResources(context.user.uid, context.tripId, placeId);
          placeData.aiResources = aiResourcesData || {};

          // Fetch deleted AI resources from separate collection
          if (loadDeletedResourcesFromFirebase) {
            try {
              const deletedResourcesData = await loadDeletedResourcesFromFirebase(
                context.user.uid,
                context.tripId,
                placeId
              );
              placeData.deletedAIResources = Array.isArray(deletedResourcesData) ? deletedResourcesData : [];
            } catch (deletedError) {
              console.warn('Could not retrieve deleted AI resources:', deletedError);
              placeData.deletedAIResources = [];
            }
          } else {
            placeData.deletedAIResources = [];
          }

          console.log('📋 [PDF] Loaded AI resources for place:', {
            placeId,
            hasActiveResources: !!placeData.aiResources && Object.keys(placeData.aiResources).length > 0,
            hasDeletedResources: placeData.deletedAIResources.length > 0,
            activeCategories: Object.keys(placeData.aiResources),
            deletedCount: placeData.deletedAIResources.length
          });
        } else {
          placeData.aiResources = {};
          placeData.deletedAIResources = [];
        }
      } catch (error) {
        console.warn('Could not retrieve AI resources for place details:', error);
        placeData.aiResources = {};
        placeData.deletedAIResources = [];
      }
    }

    return placeData;
  }

  async extractTravelListData(context) {
    try {
      // Get travel data from the actual travelService if available
      if (travelService && context.tripId && context.user?.uid) {
        const travelData = await travelService.fetchTravel(context.user.uid, context.tripId);
        return Array.isArray(travelData) ? travelData : [];
      }

      // Fallback to context data or local storage
      let data = context.data?.travel || context.trip?.travel || [];

      if (!data || data.length === 0) {
        try {
          const storedData = this.getFromLocalStorage('trip-travel');
          if (storedData) {
            data = JSON.parse(storedData);
          }
        } catch (error) {
          console.warn('Could not retrieve travel data from local storage:', error);
        }
      }

      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('Could not retrieve travel data from service:', error);
      return [];
    }
  }

  async extractTravelDetailData(travelId, context) {
    const travelData = await this.extractTravelListData(context);
    return travelData.find(travel => travel.id === travelId) || null;
  }

  async extractAccommodationListData(context) {
    try {
      // Get accommodation data from the actual accommodationService if available
      if (accommodationService && context.tripId && context.user?.uid) {
        const accommodationData = await accommodationService.fetchAccommodation(context.user.uid, context.tripId);
        return Array.isArray(accommodationData) ? accommodationData : [];
      }

      // Fallback to context data or local storage
      let data = context.data?.accommodation || context.trip?.accommodation || [];

      if (!data || data.length === 0) {
        try {
          const storedData = this.getFromLocalStorage('trip-accommodation');
          if (storedData) {
            data = JSON.parse(storedData);
          }
        } catch (error) {
          console.warn('Could not retrieve accommodation data from local storage:', error);
        }
      }

      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('Could not retrieve accommodation data from service:', error);
      return [];
    }
  }

  async extractAccommodationDetailData(accommodationId, context) {
    console.log('🏨 [DEBUG] extractAccommodationDetailData called:', {
      accommodationId,
      contextTrip: context.trip?.name,
      contextTripId: context.tripId,
      contextUserId: context.user?.uid
    });

    const accommodationData = await this.extractAccommodationListData(context);
    console.log('🏨 [DEBUG] extractAccommodationListData result:', {
      totalAccommodations: accommodationData?.length || 0,
      accommodationIds: accommodationData?.map(acc => acc.id) || []
    });

    const accommodationDetail = accommodationData.find(acc => acc.id === accommodationId) || null;

    console.log('🏨 [DEBUG] Found accommodation detail:', {
      found: !!accommodationDetail,
      accommodationId,
      accommodationName: accommodationDetail?.details?.name || accommodationDetail?.summary,
      hasDetails: !!accommodationDetail?.details,
      detailsKeys: accommodationDetail?.details ? Object.keys(accommodationDetail.details) : [],
      fullDataStructure: accommodationDetail ? {
        id: accommodationDetail.id,
        summary: accommodationDetail.summary,
        details: accommodationDetail.details,
        hasContentBlocks: !!accommodationDetail.contentBlocks,
        hasNotes: !!accommodationDetail.notes,
        otherKeys: Object.keys(accommodationDetail).filter(k => !['id', 'summary', 'details', 'contentBlocks', 'notes'].includes(k))
      } : null
    });

    if (accommodationDetail) {
      // Also get days data for Related Days section (same as AccommodationDetails.jsx useDays hook)
      try {
        const daysData = await this.extractDaysListData(context);
        accommodationDetail._daysData = daysData; // Add days data to accommodation data for PDF generation
        console.log('🏨 [DEBUG] Added days data to accommodation detail:', {
          accommodationId,
          daysCount: daysData?.length || 0
        });
      } catch (error) {
        console.warn('Could not retrieve days data for accommodation details:', error);
        accommodationDetail._daysData = [];
      }

      console.log('🏨 [DEBUG] Final accommodation data structure:', {
        keys: Object.keys(accommodationDetail),
        detailsStructure: accommodationDetail.details ? {
          name: accommodationDetail.details.name,
          address: accommodationDetail.details.address,
          checkInDate: accommodationDetail.details.checkInDate,
          checkOutDate: accommodationDetail.details.checkOutDate,
          phone: accommodationDetail.details.phone,
          website: accommodationDetail.details.website,
          bookingReference: accommodationDetail.details.bookingReference,
          bookingAgent: accommodationDetail.details.bookingAgent,
          bookingUrl: accommodationDetail.details.bookingUrl,
          cost: accommodationDetail.details.cost,
          status: accommodationDetail.details.status,
          allKeys: Object.keys(accommodationDetail.details)
        } : null
      });
    }

    return accommodationDetail;
  }

  async extractDocumentsListData(context) {
    try {
      // Get documents data from the actual documentService if available
      if (documentService && context.tripId && context.user?.uid) {
        const documentsData = await documentService.getDocuments(context.user.uid, context.tripId);
        return Array.isArray(documentsData) ? documentsData : [];
      }

      // Fallback to context data or local storage
      let data = context.data?.documents || context.trip?.documents || [];

      if (!data || data.length === 0) {
        try {
          const storedData = this.getFromLocalStorage('trip-documents');
          if (storedData) {
            data = JSON.parse(storedData);
          }
        } catch (error) {
          console.warn('Could not retrieve documents data from local storage:', error);
        }
      }

      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('Could not retrieve documents data from service:', error);
      return [];
    }
  }

  async extractDocumentDetailData(documentId, context) {
    const documentsData = await this.extractDocumentsListData(context);
    return documentsData.find(doc => doc.id === documentId) || null;
  }

  async extractExtrasListData(context) {
    let data = context.data?.extras || context.trip?.extras || [];

    if (!data || data.length === 0) {
      try {
        const storedData = this.getFromLocalStorage('trip-extras');
        if (storedData) {
          data = JSON.parse(storedData);
        }
      } catch (error) {
        console.warn('Could not retrieve extras data from local storage:', error);
      }
    }

    return Array.isArray(data) ? data : [];
  }

  // Utility methods

  /**
   * Get data from local storage safely
   * @param {string} key - Storage key
   * @returns {string|null} Stored data or null
   */
  getFromLocalStorage(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn(`Could not access localStorage for key ${key}:`, error);
      return null;
    }
  }

  /**
   * Check if page type is supported for PDF export
   * @param {string} pageType - Page type to check
   * @returns {boolean} Whether page type is supported
   */
  isSupported(pageType) {
    return this.supportedPages.includes(pageType);
  }

  /**
   * Get list of all supported page types
   * @returns {Array<string>} Supported page types
   */
  getSupportedPages() {
    return [...this.supportedPages];
  }

  /**
   * Sanitize text content for PDF export to prevent encoding issues
   * @param {string} text - Text content to sanitize
   * @returns {string} Sanitized text
   */
  sanitizeTextForPDF(text) {
    if (!text || typeof text !== 'string') {
      return '';
    }

    console.log('Sanitizing text for PDF:', {
      originalText: text,
      containsProblematicChars: /[Ø<>&è]/.test(text)
    });

    // Remove or replace problematic characters that cause encoding issues
    let cleanText = text;

    // Replace problematic Unicode characters that jsPDF can't handle well
    cleanText = cleanText.replace(/[Ø]/g, 'O'); // Ø -> O
    cleanText = cleanText.replace(/[è]/g, 'e'); // è -> e
    cleanText = cleanText.replace(/[é]/g, 'e'); // é -> e
    cleanText = cleanText.replace(/[à]/g, 'a'); // à -> a
    cleanText = cleanText.replace(/[ü]/g, 'u'); // ü -> u
    cleanText = cleanText.replace(/[ö]/g, 'o'); // ö -> o
    cleanText = cleanText.replace(/[ä]/g, 'a'); // ä -> a
    cleanText = cleanText.replace(/[ß]/g, 'ss'); // ß -> ss

    // Remove or replace other problematic characters
    cleanText = cleanText.replace(/[<>&]/g, (match) => {
      switch (match) {
        case '<': return '(less than)';
        case '>': return '(greater than)';
        case '&': return 'and';
        default: return match;
      }
    });

    // Ensure proper line endings
    cleanText = cleanText.replace(/\r\n/g, '\n');
    cleanText = cleanText.replace(/\r/g, '\n');

    // Remove non-printable characters that might cause encoding issues
    cleanText = cleanText.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

    return cleanText;
  }
}

// Export singleton instance
export const pageContextDetector = new PageContextDetector();
export default pageContextDetector;